import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, CircleSlash, XCircle } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { KeyValue, Panel } from '../../ui/layout';

type Provider = 'own' | 'cloud';

interface EvalRecord {
  model: string;
  version: string | null;
  passed: number;
  total: number;
  score: number;
  at: string;
}

interface EvalOverview {
  accepted: EvalRecord | null;
  last:
    | (EvalRecord & {
        accepted: boolean;
        trigger?: 'auto' | 'manual';
        cases: EvalCase[];
      })
    | null;
  current: {
    model: string;
    version: string | null;
    reasoning?: string | null;
    checked: boolean;
    /** Допущена по текущему набору вопросов; false — по прежнему, перепроверка запустится сама */
    suiteIsCurrent?: boolean;
    lastIsCurrent: boolean;
  } | null;
  running: { startedAt: string; trigger: 'auto' | 'manual' } | null;
  suite: { total: number; minScore: number; acceptance?: number };
  titles: Record<string, string>;
  /** Вопросы приёмочного набора по документам вида пилота */
  acceptanceIds?: string[];
}

/** Облачная модель (ИИ-14): выключена, пока владелец портала не включит */
interface CloudPolicy {
  enabled: boolean;
  updatedAt: string | null;
  configured: boolean;
}

/** Итог вопроса: причина и длина ответа, без текста ответа и документа */
interface EvalCase {
  id: string;
  ok: boolean;
  error?: boolean;
  reason?: 'wrong' | 'truncated' | 'empty' | 'error';
  /** Уточнение к «неверно» (с 03.11.2026), без текста ответа */
  detail?: string;
  chars?: number;
  tokens?: number;
}

const REASONS: Record<NonNullable<EvalCase['reason']>, string> = {
  wrong: 'неверно',
  truncated: 'ответ обрезан',
  empty: 'пустой ответ',
  error: 'модель не ответила',
};

const DETAILS: Record<string, string> = {
  no_data_expected: 'ответа в документе нет, а модель не сказала «нет данных»',
  no_data_given: 'ответ в документе есть, а модель сказала «нет данных»',
  missing: 'нет ожидаемого значения',
  extra_number: 'лишнее число из документа',
  excluded: 'назвала устаревшее значение',
  too_long: 'пересказ вместо короткого ответа',
  guess: 'вместо «нет данных» — догадка',
};

/** Причина: у записей до 28.10.2026 её нет — выводится из error; уточнение — с 03.11.2026 */
const caseReason = (c: EvalCase) => {
  if (c.ok) return 'верно';
  const base = REASONS[c.reason ?? (c.error ? 'error' : 'wrong')] ?? 'неверно';
  const more = c.detail ? DETAILS[c.detail] : undefined;
  return more ? `${base}: ${more}` : base;
};

/** Длина ответа: знаки и токены, если модель их сообщила */
const caseSize = (c: EvalCase) => {
  if (c.chars === undefined) return null;
  return c.tokens !== undefined ? `${c.chars} зн., ${c.tokens} ток.` : `${c.chars} зн.`;
};

const REASONING: Record<string, string> = {
  none: 'выключены',
  low: 'короткие',
  medium: 'средние',
  high: 'подробные',
};

const PROVIDERS: Array<{ id: Provider; title: string }> = [
  { id: 'own', title: 'Своя модель' },
  { id: 'cloud', title: 'Облачная модель' },
];

const percent = (v: number) => `${Math.round(v * 100)}\u00a0%`;
const modelName = (m: { model: string; version: string | null }) =>
  m.version ? `${m.model}, версия ${m.version}` : m.model;

/**
 * Кабинет владельца портала → Pilot (ИИ-13): какая модель настроена, допущена ли она к вопросам, итог последней
 * контрольной проверки по вопросам и кнопка «Проверить модель». Проверка запускается и сама — после смены
 * адреса, имени или версии модели.
 */
export function PilotPage() {
  return (
    <div className="flex flex-col gap-6">
      <p className="text-[14px] text-ink-2">
        Pilot отвечает на вопросы только моделью, которая прошла контрольную проверку: вопросы по вымышленным
        документам с известными ответами. После смены модели проверка запускается сама; запустить её заново
        можно кнопкой. Если сервер перезапустился во время проверки, повторный запуск станет доступен через
        несколько минут. Записи о проверках — в{' '}
        <Link to="/platform/events" className="underline underline-offset-2">
          журнале
        </Link>
        .
      </p>
      {PROVIDERS.map((p) => (
        <ProviderPanel key={p.id} provider={p.id} title={p.title} />
      ))}
      <CloudPanel />
    </div>
  );
}

/**
 * Облачная модель (ИИ-14): включает и выключает только владелец портала. Запросы с персональными данными и
 * суммами в облако не уходят никогда; при своей модели облако не используется.
 */
function CloudPanel() {
  const qc = useQueryClient();
  const key = ['platform', 'pilot-cloud'];
  const [confirm, setConfirm] = useState(false);
  const q = useQuery({ queryKey: key, queryFn: () => api<CloudPolicy>('/pilot/cloud') });
  const save = useMutation({
    mutationFn: (enabled: boolean) => api<CloudPolicy>('/pilot/cloud', { method: 'PUT', body: { enabled } }),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      setConfirm(false);
      void qc.invalidateQueries({ queryKey: ['platform', 'pilot-eval', 'cloud'] });
      toast(data.enabled ? 'Облачная модель включена' : 'Облачная модель выключена');
    },
  });
  const title = 'Решение об облачной модели';
  if (q.isPending)
    return (
      <Panel title={title}>
        <Spinner />
      </Panel>
    );
  if (q.isError)
    return (
      <Panel title={title}>
        <ErrorBox error={q.error} />
      </Panel>
    );
  const c = q.data;
  return (
    <Panel
      title={title}
      actions={
        c.enabled ? (
          <Button loading={save.isPending} onClick={() => save.mutate(false)}>
            Выключить облачную модель
          </Button>
        ) : (
          <Button onClick={() => setConfirm(true)}>Включить облачную модель</Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {save.isError ? <ErrorBox error={save.error} /> : null}
        <KeyValue
          items={[
            {
              label: 'Состояние',
              value: c.enabled ? (
                <Badge tone="warning">Включена владельцем портала</Badge>
              ) : (
                <Badge>Выключена</Badge>
              ),
            },
            { label: 'Изменено', value: c.updatedAt ? dateTime(c.updatedAt) : 'не менялось' },
            {
              label: 'Адрес облачной модели',
              value: c.configured ? 'задан в настройках сервера' : 'не задан — включение ничего не изменит',
            },
          ]}
        />
        <p className="text-[14px] text-ink-2">
          Облачная модель отвечает, только если своей модели нет. В облако не уходят вопросы, которые раздел
          отметил как содержащие персональные данные или суммы, и тексты, где найдены суммы, ИИН, номера
          удостоверений, даты рождения или оклады. Облачная модель тоже проходит контрольную проверку.
          Включение и выключение записываются в журнал.
        </p>
      </div>
      <Dialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Включить облачную модель?"
        width="sm"
        footer={
          <>
            <Button onClick={() => setConfirm(false)}>Отмена</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(true)}>
              Включить
            </Button>
          </>
        }
      >
        <p className="text-[14px] text-ink">
          Вопросы без персональных данных и сумм будут уходить во внешнюю модель, пока своей модели нет.
          Решение записывается в журнал владельца портала; выключить можно в любой момент.
        </p>
      </Dialog>
    </Panel>
  );
}

function ProviderPanel({ provider, title }: { provider: Provider; title: string }) {
  const qc = useQueryClient();
  const key = ['platform', 'pilot-eval', provider];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<EvalOverview>('/pilot/eval', { query: { provider } }),
    // Пока идёт проверка — обновлять каждые 3 секунды, чтобы итог появился сам
    refetchInterval: (query) => (query.state.data?.running ? 3000 : false),
  });
  const start = useMutation({
    mutationFn: () =>
      api<EvalOverview>('/pilot/eval', { method: 'POST', body: { provider, background: true } }),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      toast('Проверка модели запущена');
    },
    onError: () => void qc.invalidateQueries({ queryKey: key }),
  });

  if (q.isPending)
    return (
      <Panel title={title}>
        <Spinner />
      </Panel>
    );
  if (q.isError)
    return (
      <Panel title={title}>
        <ErrorBox error={q.error} />
      </Panel>
    );

  const o = q.data;
  // Облачная модель не настроена — блока нет, чтобы не путать; своя показывается всегда
  if (!o.current && provider === 'cloud') return null;

  const state = !o.current ? (
    <Badge>Не настроена — Pilot не отвечает</Badge>
  ) : o.running ? (
    <Badge tone="info">Идёт проверка</Badge>
  ) : o.current.checked && o.current.suiteIsCurrent === false ? (
    <Badge tone="warning">Перепроверка по новому набору</Badge>
  ) : o.current.checked ? (
    <Badge tone="success">Допущена к вопросам</Badge>
  ) : o.current.lastIsCurrent ? (
    <Badge tone="danger">Не прошла проверку — Pilot не отвечает</Badge>
  ) : (
    <Badge tone="warning">Не проверена — Pilot не отвечает</Badge>
  );

  const last = o.last;
  // Итог приёмочного набора отдельно: по нему видно, как модель читает документы вида пилота
  const accIds = new Set(o.acceptanceIds ?? []);
  const accCases = last ? last.cases.filter((c) => accIds.has(c.id)) : [];
  return (
    <Panel
      title={title}
      meta={o.current ? modelName(o.current) : undefined}
      actions={
        o.current ? (
          <Button
            variant={provider === 'own' ? 'primary' : 'secondary'}
            loading={start.isPending || Boolean(o.running)}
            onClick={() => start.mutate()}
          >
            {o.running ? 'Идёт проверка' : 'Проверить модель'}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-5">
        {start.isError ? <ErrorBox error={start.error} /> : null}
        <KeyValue
          items={[
            { label: 'Состояние', value: state },
            {
              label: 'Проверка идёт',
              value: o.running
                ? `с ${dateTime(o.running.startedAt)}, ${o.running.trigger === 'auto' ? 'запущена сама после смены модели' : 'запущена владельцем портала'}`
                : 'нет',
            },
            ...(o.current
              ? [
                  {
                    label: 'Рассуждения модели',
                    value: o.current.reasoning
                      ? (REASONING[o.current.reasoning] ?? o.current.reasoning)
                      : 'как задано в самой модели',
                  },
                ]
              : []),
            {
              label: 'Последняя принятая',
              value: o.accepted
                ? `${modelName(o.accepted)} — ${o.accepted.passed} из ${o.accepted.total} (${percent(o.accepted.score)}), ${dateTime(o.accepted.at)}`
                : 'ни одна модель ещё не прошла проверку',
            },
            {
              label: 'Правило',
              value: `не меньше ${percent(o.suite.minScore)} верных ответов из ${o.suite.total} и не хуже допущенной модели${o.suite.acceptance ? `; в наборе ${o.suite.acceptance} вопросов по документам вида пилота (КП, счёт, накладная, акт, письмо, протокол)` : ''}`,
            },
          ]}
        />
        {last ? (
          <div className="flex flex-col gap-3">
            <h3 className="text-[15px] font-semibold text-ink">
              Последняя проверка: {modelName(last)}, {dateTime(last.at)}
            </h3>
            <p className="text-[14px] text-ink">
              Верных ответов {last.passed} из {last.total} ({percent(last.score)}).{' '}
              {accCases.length ? (
                <>
                  По документам вида пилота: {accCases.filter((c) => c.ok).length} из {accCases.length}.{' '}
                </>
              ) : null}
              {last.accepted ? (
                <span className="text-success">Решение: модель допущена.</span>
              ) : (
                <span className="text-danger">Решение: модель не допущена, Pilot ею не отвечает.</span>
              )}{' '}
              <span className="text-ink-2">
                {last.trigger === 'auto'
                  ? 'Запущена сама после смены модели.'
                  : 'Запущена владельцем портала.'}
              </span>
            </p>
            {last.cases.some((c) => c.reason === 'truncated' || c.reason === 'empty') ? (
              <p className="text-[14px] text-warning">
                Часть ответов обрезана или пуста: модель отвечает слишком длинно или тратит предел длины
                ответа на рассуждения. Такие ответы не засчитываются; проверьте режим рассуждений модели.
              </p>
            ) : null}
            <ul className="divide-y divide-line rounded-md border border-line">
              {last.cases.map((c) => (
                <li key={c.id} className="flex min-h-11 items-center gap-3 px-4 py-2 text-[14px]">
                  {c.ok ? (
                    <CheckCircle2 className="size-4 shrink-0 text-success" aria-label="верно" />
                  ) : c.error || c.reason === 'truncated' || c.reason === 'empty' ? (
                    <CircleSlash className="size-4 shrink-0 text-warning" aria-label={caseReason(c)} />
                  ) : (
                    <XCircle className="size-4 shrink-0 text-danger" aria-label="неверно" />
                  )}
                  <span className="min-w-0 flex-1 text-ink">{o.titles[c.id] ?? c.id}</span>
                  <span className="flex max-w-[55%] flex-col items-end text-right text-[13px] text-ink-2">
                    <span>{caseReason(c)}</span>
                    {caseSize(c) ? <span className="text-[12px]">{caseSize(c)}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-[14px] text-ink-2">Проверок этой модели ещё не было.</p>
        )}
      </div>
    </Panel>
  );
}
